import { createTheme } from '@mui/material/styles';

// MHCL Brand Colors - Official Brand Guidelines
const brandColors = {
  // Brand Primary - Teal
  brand: {
    50: '#e0f7f8',
    100: '#b3ebec',
    200: '#80dedf',
    300: '#4dd0d2',
    400: '#26c5c8',
    500: '#00989f', // Main brand color
    600: '#008a90',
    700: '#007a7f',
    800: '#006a6f',
    900: '#005252',
  },
  // Alt Brand - Darker Teal
  altBrand: {
    50: '#e0f4f5',
    100: '#b3e4e6',
    200: '#80d2d5',
    300: '#4dc0c4',
    400: '#26b2b7',
    500: '#03777d', // Alt brand color
    600: '#036f75',
    700: '#02646a',
    800: '#025a60',
    900: '#014347',
  },
  // Text Colors
  heading: '#071f1b',
  text: '#3c4040',
  // Background Colors
  primary: '#f0f6f7',
  secondary: '#ffffff',
  // Utility Colors
  border: '#c0c0c0',
  subtle: '#021315',
};

// Typography configuration for MHCL - Using Manrope as per brand guidelines
const typography = {
  fontFamily: '"Manrope", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
    lineHeight: 1.2,
  },
  h2: {
    fontWeight: 600,
    fontSize: '2rem',
    lineHeight: 1.3,
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
    lineHeight: 1.3,
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.4,
  },
  h5: {
    fontWeight: 500,
    fontSize: '1.25rem',
    lineHeight: 1.4,
  },
  h6: {
    fontWeight: 500,
    fontSize: '1.125rem',
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  button: {
    fontWeight: 500,
    textTransform: 'none',
  },
};

// Common component overrides
const getComponentOverrides = (mode) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
        },
      },
      contained: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: mode === 'dark' 
          ? '0 4px 6px rgba(0,0,0,0.3)' 
          : '0 2px 8px rgba(0,0,0,0.1)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: mode === 'dark' 
          ? '0 4px 6px rgba(0,0,0,0.3)' 
          : '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: mode === 'dark' 
            ? '0 8px 16px rgba(0,0,0,0.4)' 
            : '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: mode === 'dark' 
          ? '0 2px 4px rgba(0,0,0,0.3)' 
          : '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: '0px !important',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: '0px !important',
      },
    },
  },
});

// Light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.brand[500], // #00989f
      light: brandColors.brand[300],
      dark: brandColors.altBrand[500], // #03777d
      contrastText: '#ffffff',
    },
    secondary: {
      main: brandColors.altBrand[500], // #03777d
      light: brandColors.altBrand[300],
      dark: brandColors.altBrand[700],
      contrastText: '#ffffff',
    },
    background: {
      default: brandColors.primary, // #f0f6f7
      paper: brandColors.secondary, // #ffffff
    },
    text: {
      primary: brandColors.heading, // #071f1b
      secondary: brandColors.text, // #3c4040
    },
    divider: brandColors.border, // #c0c0c0
    success: {
      main: brandColors.brand[600],
    },
    warning: {
      main: '#ffa726',
    },
    error: {
      main: '#ef5350',
    },
  },
  typography,
  components: getComponentOverrides('light'),
  shape: {
    borderRadius: 8,
  },
  cssVariables: true, // Enable CSS custom properties
});

// Dark theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brandColors.brand[400], // Lighter brand color for dark mode
      light: brandColors.brand[200],
      dark: brandColors.brand[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: brandColors.altBrand[400],
      light: brandColors.altBrand[200],
      dark: brandColors.altBrand[600],
      contrastText: '#ffffff',
    },
    background: {
      default: brandColors.subtle, // #021315
      paper: '#1a2e30', // Darker version of brand colors
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#cbd5e1',
    },
    divider: '#334155',
    success: {
      main: brandColors.brand[400],
    },
    warning: {
      main: '#ffb74d',
    },
    error: {
      main: '#ef5350',
    },
  },
  typography,
  components: getComponentOverrides('dark'),
  shape: {
    borderRadius: 8,
  },
  cssVariables: true, // Enable CSS custom properties
});

const themes = { lightTheme, darkTheme };
export default themes;